import { getDefaults } from 'utils/schema/zodUtils';
import { z } from 'zod';
import { DATE_FORMAT_REQUEST, supportedCurrencyCodes } from 'data/constants';
import moment from 'moment';
import { currencyHelper } from 'utils/currencyHelper';
import { MAX_AMOUNT } from './constants';

const voucherFormSchema = z
  .object({
    amount: z.string().default('50'),
    currency: z.enum(supportedCurrencyCodes).default('GBP'),
    creator_name: z
      .string()
      .min(1, { message: 'Please enter your name' })
      .default(''),
    creator_email: z
      .string()
      .email({ message: 'Please enter a valid email' })
      .default(''),
    self_send: z.boolean().default(false),
    recipient_name: z.string().optional().default(''),
    recipient_email: z
      .string()
      .email({ message: 'Please enter a valid email' })
      .default(''),
    send_date_option: z.enum(['now', 'later']).default('now'),
    send_date: z.string().default(new Date().toString()),
    message: z.string().default(''),
    custom_gift_card_id: z.number().optional(),
  })
  .superRefine(({ amount, send_date, send_date_option, currency }, ctx) => {
    const amountNumber = Number(amount.replace(/,/g, ''));

    if (amountNumber < 50) {
      const minimumAmountText = currencyHelper(50, currency);
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `The minimum amount for a gift card is ${minimumAmountText}`,
        path: ['amount'],
      });
    }

    if (amountNumber > MAX_AMOUNT) {
      const maximumAmountText = currencyHelper(MAX_AMOUNT, currency);
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `The maximum amount for a gift card is ${maximumAmountText}`,
        path: ['amount'],
      });
    }

    if (send_date_option === 'now') {
      return z.NEVER;
    }

    const formattedSendDate = moment(send_date, DATE_FORMAT_REQUEST, true);

    if (!formattedSendDate.isValid()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid date',
        path: ['send_date'],
      });
    }

    const isPastDate = formattedSendDate.isBefore(moment(), 'day');

    if (isPastDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid date',
        path: ['send_date'],
      });
    }
  });

const defaultVoucher = getDefaults<typeof voucherFormSchema>(voucherFormSchema);

const multiVoucherFormSchema = z.object({
  vouchers: z.array(voucherFormSchema).min(1).default([defaultVoucher]),
});

const defaultMultiVoucher = getDefaults<typeof multiVoucherFormSchema>(
  multiVoucherFormSchema
);

type MultiVoucherFormSchema = z.infer<typeof multiVoucherFormSchema>;

export {
  type MultiVoucherFormSchema,
  defaultMultiVoucher,
  defaultVoucher,
  multiVoucherFormSchema,
};
